/**
 * The builder's screen order, as a doubly linked list walked by `NavigationHandler`.
 *
 * There are two flows, and which one a reader gets is decided by the experience
 * level they picked on `/resume/select-experience` — see `router/sectionOrder.ts`,
 * which every other view and list reads the same answer from:
 *
 *   fresher      contact -> education -> work history -> skills -> summary -> review
 *   experienced  contact -> work history -> education -> skills -> summary -> review
 *
 * Both flows share their `NavigationProperty` values and differ only in how the
 * nodes are chained, so `description` (the Next/Previous button labels) stays
 * consistent across the two.
 */

export class NavigatorNode {
	value: NavigationProperty;
	previous: NavigatorNode | null;
	next: NavigatorNode | null;

	constructor(value: NavigationProperty, previous: NavigatorNode | null, next: NavigatorNode | null) {
		this.value = value;
		this.previous = previous;
		this.next = next;
	}

	setNext(next: NavigatorNode | null) {
		this.next = next;
	}

	setPrevious(previous: NavigatorNode | null) {
		this.previous = previous;
	}
}

export class NavigationProperty {
	url: string;
	name: string;
	description: string;
	isComplete: boolean;

	constructor(name: string, url: string, description: string) {
		this.url = url;
		this.name = name;
		this.description = description;
		this.isComplete = false;
	}
}

export class NavigationList {
	header: NavigatorNode | null;
	current: NavigatorNode | null;

	constructor(header: NavigatorNode | null, current: NavigatorNode | null) {
		this.header = header;
		this.current = current;
	}
}

//TODO: Refactor to Include Button Names, descriptions etc.
export class NavigationFlow {
	fresherFlow: NavigatorNode;
	experiencedFlow: NavigatorNode;

	contactInformation = new NavigationProperty("heading", "contact-information", "Contact information");
	workHistory = new NavigationProperty("workhistory", "work-history", "Position");
	workDetails = new NavigationProperty("workhistory", "work-details", "Position details");
	worksummary = new NavigationProperty("workhistory", "work-history-summary", "Work history");
	educationInformation = new NavigationProperty("education", "education-information", "Education Information");
	educationSummary = new NavigationProperty("education", "education-summary", "Education Summary");
	skills = new NavigationProperty("skills", "skills", "skills");
	briefSummary = new NavigationProperty("summary", "brief-summary", "Brief Summary");
	review = new NavigationProperty("finalize", "review", "Review");

	constructor() {
		this.fresherFlow = this.buildFlow(true);
		this.experiencedFlow = this.buildFlow(false);
	}

	/** The flow for a reader, ready to hand to `NavigationList`. */
	getFlow(educationFirst: boolean): NavigatorNode {
		return educationFirst ? this.fresherFlow : this.experiencedFlow;
	}

	/** Chain the screens into a linked list and return its head. */
	private buildFlow(educationFirst: boolean): NavigatorNode {
		const education = [this.educationInformation, this.educationSummary];
		const workHistory = [this.workHistory, this.workDetails, this.worksummary];

		const screens: NavigationProperty[] = [
			this.contactInformation,
			...(educationFirst ? [...education, ...workHistory] : [...workHistory, ...education]),
			this.skills,
			this.briefSummary,
			this.review,
		];

		const header = new NavigatorNode(screens[0], null, null);
		let previous = header;

		for (const screen of screens.slice(1)) {
			const node = new NavigatorNode(screen, previous, null);
			previous.setNext(node);
			previous = node;
		}

		return header;
	}
}
